import {
    GridCallbackDetails, GridFilterItem,
    GridFilterModel,
    GridLogicOperator
} from "@mui/x-data-grid-premium";
import {action, computed, makeObservable, observable, toJS} from "mobx";
import {muiXItemMatches, setMuixFilterItem, unsureMuixFilterValue} from "../../../utils/muixUtils.ts";
import {
    CAT_EQ_OPERATOR,
    CAT_NEQ_OPERATOR
} from "../../../pages/categorization/components/review-table/review-table-muix/filters/CategoryFilter.tsx";
import {
    CATEGORY_FILTER_FIELD
} from "../../../pages/categorization/controllers/CategorizationReviewPageFilterController.ts";
import {BaseMuixTableController, GROUP_COLUMN_INTERPOLATION} from "./BaseMuixTableController.tsx";

export const MUIX_EMPTY_FILTER_MODEL: GridFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterValues: [],
    quickFilterLogicOperator: GridLogicOperator.And,
}

const VALID_VALUE = '1';
export const MUIX_CATEGORY_BAR_FILTER_ID = 'category_999';
export const MUIX_CATEGORY_JOIN_STRING = ' > ';
export const MUIX_CATEGORY_FILTER_FIELD = 'cat';
export const MUIX_CATEGORY_FILTER_KEY: GridFilterItem = {
    field: CATEGORY_FILTER_FIELD,
    operator: 'equals',
}

export class BaseMuixFilterController {
    selectedCategory: string[] = [];
    _muiXGridFilterModel: GridFilterModel = MUIX_EMPTY_FILTER_MODEL;

    constructor(
        private tableController: BaseMuixTableController
    ) {
        makeObservable(this, {
            selectedCategory: observable,
            _muiXGridFilterModel: observable,
            clearFilters: action,
            selectedLevel: computed,
            setSelectedCategory: action,
            onFilterModelChange: action,
            gridFilterModel: computed,
            apiGridFilterModel: computed,
            removeFilter: action,
        });
    }

    clearFilters() {
        this.selectedCategory = [];
        this._muiXGridFilterModel = MUIX_EMPTY_FILTER_MODEL;
        return true;
    }

    get selectedLevel(): number {
        return this.selectedCategory.length;
    }

    setSelectedCategory(selectedCategory: string[]) {
        this.selectedCategory = selectedCategory;
        this.tableController.resetRowState();
    }

    onFilterModelChange(model: GridFilterModel, details: GridCallbackDetails<'filter'>): void {
        console.debug('BaseMuixFilterController.onFilterModelChange', details.reason, toJS(model));
        // Beware that !! filters out empty strings as well as undefined
        const groupingAddedCols = this.tableController.rowGroupingModel.map(
            field => GROUP_COLUMN_INTERPOLATION.replace('field', field));
        let items = [...model.items].map(item => ({
            ...item,
            field: groupingAddedCols.includes(item.field) ? item.field.substring(GROUP_COLUMN_INTERPOLATION.indexOf('field'), item.field.length - 2) : item.field
        }));
        // Extract category filter
        const categoryFilterIndex = items.findIndex(i => i.id === MUIX_CATEGORY_BAR_FILTER_ID);
        if (categoryFilterIndex >= 0) {
            this.setSelectedCategory(items[categoryFilterIndex].value.split(MUIX_CATEGORY_JOIN_STRING));
            items.splice(categoryFilterIndex, 1);
        }
        if (items.filter(i => i.id !== MUIX_CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, MUIX_CATEGORY_FILTER_KEY)).length > 0) {
            if (categoryFilterIndex >= 0)
                console.error(`Category filter is being overwritten ${toJS(this.selectedCategory)}`);
            this.setSelectedCategory(items.filter(
                i => i.id !== MUIX_CATEGORY_BAR_FILTER_ID && muiXItemMatches(i, MUIX_CATEGORY_FILTER_KEY)
            ).sort(
                (a, b) => Number(Array.from(a.field).reverse()[0]) - Number(Array.from(b.field).reverse()[0])
            ).map(
                i => i.value
            ));
            items = items.filter(i => !muiXItemMatches(i, MUIX_CATEGORY_FILTER_KEY));
        }

        // Observables can only be changed in actions, so do any modifications here
        items.forEach((i, idx) => {
            if (i.operator === CAT_EQ_OPERATOR.operatorValue || i.operator === CAT_NEQ_OPERATOR.operatorValue) {
                i.value = VALID_VALUE; // Must be set to act like a normal filter item
            }
            // Ids could be messed up. Consider the following scenario:
            //  1. User adds a filter (defaults to Supplier)
            //  2. We store that filter in groupSearch and assign an id to it "group_999_n"
            //  3. User changes the field to p_id (if we don't change id, it would still be "group_999_n"
            //  4. User adds another filter for supplier. Now items would have two fields with same id "group_999_n"
            i.id = i.field + '_' + String(idx);
        });

        this._muiXGridFilterModel = {...model, items};
        this.tableController.resetRowState();
    }

    /**
     * The combined filter model for the DataGrid component
     */
    get gridFilterModel(): GridFilterModel {
        let items = [...this._muiXGridFilterModel.items];
        if (this.selectedCategory.length !== 0) {
            // Ensure the CATEGORY_FILTER_KEY is also set
            const value = this.selectedCategory.join(MUIX_CATEGORY_JOIN_STRING); // Only shown in the UI
            unsureMuixFilterValue(items, MUIX_CATEGORY_FILTER_KEY, value, MUIX_CATEGORY_BAR_FILTER_ID)
        }
        return {...this._muiXGridFilterModel, items};
    }

    /**
     * The filters (whereby the group field is replaced with GROUP_VAL)
     */
    get apiGridFilterModel(): GridFilterModel {
        let items = [...this._muiXGridFilterModel.items];
        // For the selected category, multiple filters are used, one for each level
        const apiCatFilterItems = this.selectedCategory.map((label, index) => ({
            id: `cat_l${index + 1}`, // Ids don't actually matter for the API
            field: 'l' + String(index + 1),
            operator: 'equals',
            value: label,
        }))
        setMuixFilterItem(items, MUIX_CATEGORY_FILTER_KEY, this.selectedCategory.length !== 0, ...apiCatFilterItems)
        return {...this._muiXGridFilterModel, items};
    }

    removeFilter(id: number | string | undefined) {
        if (!id) {
            console.warn(this.constructor.name + '.removeFilter: id is undefined');
            return;
        }
        const item = this.gridFilterModel.items.find(i => i.id === id);
        if (muiXItemMatches(item, MUIX_CATEGORY_FILTER_KEY)) {
            this.setSelectedCategory([]);
            this.tableController.resetRowState();
            return;
        }
        const i = this._muiXGridFilterModel.items.findIndex(item => item.id === id);
        if (i !== -1) {
            this._muiXGridFilterModel.items.splice(i, 1);
            this.tableController.resetRowState();
            return;
        }
        console.warn(this.constructor.name + '.removeFilter: filter not found', {id});
    }
}
